// A carry code's last open, kept on this device until it's added, saved or thrown away.
//
// Once a code's last open is unlocked the server copy is gone, so the unlocked saves go straight
// into a small IndexedDB store here. Whatever happens to the page that opened it (a suspended tab,
// a closed desktop window, a reload), Settings and the start page can still offer it. It's the
// student's own work on their own device: never synced, never in a save file or a backup (those
// carry localStorage only), and "Delete my uploads and files" leaves it alone. Clear site data and
// Erase everything remove it only after their own confirm. Each copy runs out after 7 days.
//
// Every copy has its own record, so a second last open never overwrites the first, and letting one
// go never touches another. Up to MAX wait at once: the carry sheet won't unlock another code until
// one of them is dealt with (park itself never turns one away, so nothing is ever lost here).
export const DB = "revault-carry-held";
export const FLAG = "revault:carry-held"; // "newest parked time:how many"; lets the start page check without opening the database
export const KEEP_MS = 7 * 24 * 60 * 60 * 1000;
export const MAX = 3;
const STORE = "held";

let idbImpl = null;
let lsImpl = null;
// Tests hand in an in-memory IndexedDB and storage.
export function _env(idb, ls) { idbImpl = idb || null; lsImpl = ls || null; }
const idb = () => idbImpl || (typeof indexedDB !== "undefined" ? indexedDB : null);
const ls = () => { try { return lsImpl || (typeof localStorage !== "undefined" ? localStorage : null); } catch { return null; } };

function setFlag(t, n = 1) { try { const s = ls(); if (s) { if (t) s.setItem(FLAG, t + ":" + Math.max(1, n | 0)); else s.removeItem(FLAG); } } catch { /* storage off: Settings still finds it */ } }
// { t, n } from the flag. An older flag is the time alone, which means one copy.
function readFlag() {
	let v = "";
	try { const s = ls(); v = s ? String(s.getItem(FLAG) || "") : ""; } catch { v = ""; }
	const [a, b] = v.split(":");
	return { t: Number(a) || 0, n: Math.max(1, parseInt(b, 10) || 1) };
}

// When the start page should mention it: something parked under 7 days ago (the flag alone, no database).
export function heldSince(now = Date.now()) {
	const { t } = readFlag();
	return t > 0 && now - t < KEEP_MS && t <= now + 60000 ? t : 0;
}
// How many copies the start page should mention (0 when heldSince() is 0).
export function heldNumber(now = Date.now()) { return heldSince(now) ? readFlag().n : 0; }

const newId = (now) => now.toString(36) + "-" + Math.random().toString(36).slice(2, 10);

// create: false opens it only if it's already there (an upgrade means it wasn't, so it's aborted).
// Resolves { db } or { db: null, missing }, missing being true only when there's surely nothing kept:
// no database at all, or no IndexedDB in this browser. A failed or blocked open isn't that.
function open(create) {
	return new Promise((res) => {
		const f = idb();
		if (!f) return res({ db: null, missing: true });
		let req, missing = false;
		try { req = f.open(DB, 1); } catch { return res({ db: null, missing: false }); }
		req.onupgradeneeded = () => {
			if (!create) { missing = true; try { req.transaction.abort(); } catch { /* never create it */ } return; }
			try { req.result.createObjectStore(STORE); } catch { /* already there */ }
		};
		req.onerror = () => res({ db: null, missing });
		req.onblocked = () => res({ db: null, missing: false });
		req.onsuccess = () => res({ db: req.result, missing: false });
	});
}

// fn gets the store and returns a function that reads its results once the transaction is done.
function run(db, mode, fn) {
	return new Promise((res) => {
		try {
			const t = db.transaction(STORE, mode);
			const read = fn(t.objectStore(STORE));
			t.oncomplete = () => { try { db.close(); } catch { /* closed */ } let value; try { value = read ? read() : undefined; } catch { value = undefined; } res({ ok: true, value }); };
			t.onerror = t.onabort = () => { try { db.close(); } catch { /* closed */ } res({ ok: false }); };
		} catch { try { db.close(); } catch { /* closed */ } res({ ok: false }); }
	});
}

// Every record with its key as its id (a copy parked before ids existed keeps its old key).
function all(s) {
	const keys = s.getAllKeys();
	const vals = s.getAll();
	return () => (vals.result || []).map((v, i) => (v && typeof v === "object" ? Object.assign({}, v, { id: String(keys.result[i]) }) : null));
}
const valid = (rec) => !!(rec && rec.data && typeof rec.data === "object" && !Array.isArray(rec.data));
const newest = (recs) => recs.reduce((m, r) => Math.max(m, Number(r.t) || 0), 0);

// Keep the unlocked saves. data is the envelope's data; made its "YYYY-MM-DD", if any.
// Resolves the new copy's id, or "" when this browser couldn't keep it.
export async function park(data, made, now = Date.now()) {
	const { db } = await open(true);
	if (!db) return "";
	const id = newId(now);
	const rec = { id, t: now, made: typeof made === "string" ? made : null, data };
	const r = await run(db, "readwrite", (s) => { s.put(rec, id); return all(s); });
	if (!r.ok) return "";
	const recs = (r.value || []).filter(valid);
	setFlag(newest(recs) || now, recs.length);
	return id;
}

// { ok, recs }: ok is false when the copies couldn't be read just now (they may still be there).
async function read(now) {
	const { db, missing } = await open(false);
	if (!db) { if (missing) setFlag(0); return { ok: missing, recs: [] }; }
	let r;
	try { r = db.objectStoreNames.contains(STORE) ? await run(db, "readonly", all) : (db.close(), { ok: true, value: [] }); }
	catch { r = { ok: false }; }
	if (!r.ok) return { ok: false, recs: [] };
	const recs = r.value || [];
	const keep = [], gone = [];
	for (const rec of recs) {
		if (valid(rec) && now - (Number(rec.t) || 0) < KEEP_MS) keep.push(rec);
		else if (rec && rec.id) gone.push(rec.id);
	}
	for (const id of gone) await drop(id);
	keep.sort((a, b) => (Number(a.t) || 0) - (Number(b.t) || 0));
	setFlag(newest(keep), keep.length);
	return { ok: true, recs: keep };
}

// Every kept copy { id, t, made, data }, oldest first. Ones older than 7 days are thrown away here.
export async function list(now = Date.now()) { return (await read(now)).recs; }

// How many copies wait, or -1 when that couldn't be read just now.
export async function count(now = Date.now()) { const r = await read(now); return r.ok ? r.recs.length : -1; }

// The oldest kept copy, or null.
export async function peek(now = Date.now()) { const l = await list(now); return l[0] || null; }

// That one copy was added, kept as a file or text, or thrown away: it goes, and only it.
export async function drop(id) {
	if (!id) return;
	const { db, missing } = await open(false);
	if (!db) { if (missing) setFlag(0); return; }
	if (!db.objectStoreNames.contains(STORE)) { try { db.close(); } catch { /* closed */ } return; }
	const r = await run(db, "readwrite", (s) => { s.delete(String(id)); return all(s); });
	if (r.ok) { const recs = (r.value || []).filter(valid); setFlag(newest(recs), recs.length); }
}

// Clear site data and Erase everything, after their own confirm: every copy.
export function dropAll() {
	setFlag(0);
	return new Promise((res) => {
		const f = idb();
		if (!f) return res();
		let req;
		try { req = f.deleteDatabase(DB); } catch { return res(); }
		req.onsuccess = req.onerror = () => res();
		req.onblocked = () => res(); // finishes on its own once the other page lets go
	});
}

// The save file a kept copy downloads as (the same shape the carry sheet makes).
export const saveBlobOf = (rec) => ({ _rv: "save", v: 1, data: rec.data });

// "the carry copy that wasn't added yet, and that code" / "the 2 carry copies ..., and those codes".
// A count that couldn't be read (-1) gets "any carry copies ...".
export const heldWords = (n) => (n === 1
	? "the carry copy that wasn't added yet, and that code can't be opened again"
	: n > 1 ? "the " + n + " carry copies that weren't added yet, and those codes can't be opened again"
		: "any carry copies that weren't added yet, and those codes can't be opened again");
